import React, { Component, Fragment } from "reactn";
import styled from "styled-components";
import { Button, Icon, Popover } from "@material-ui/core";

import { app } from "js/namespaces";
import { ds } from "js/core/models/dataService";
import { $, _ } from "js/vendor";
import log from "js/core/logger";
import { PREDEFINED_PALETTES } from "common/constants";
import { SharedTheme } from "js/core/models/sharedTheme";
import { themeColors } from "js/react/sharedStyles";
import { Theme } from "js/core/models/theme";
import { ShowConfirmationDialog } from "js/react/components/Dialogs/BaseDialog";
import { UIController } from "js/editor/dialogs/UIController";

import { BlueButton, UIPane, UIPaneContents } from "js/react/components/UiComponents";
import { FlexBox, ScrollBox } from "js/react/components/LayoutGrid";
import { ThumbnailGrid } from "js/react/views/AddSlide/Panes/Components/ThumbnailGrid";
import { FlexSpacer, Gap20, Gap30 } from "js/react/components/Gap";
import { ThemeThumbnail } from "js/editor/ThemeUI/ThemeThumbnail";
import { PreviewSlide1, PreviewSlide2, PreviewSlide3, PreviewSlide4, PreviewSlide5 } from "js/editor/previewSlides";
import { EditThemePanel } from "../editorPanels/editThemePanel";
import { defaultSimpleTheme } from "common/themes";
import importPPTTheme from "js/core/utilities/importPPTTheme";
import { trackActivity } from "js/core/utilities/utilities";
import CanvasPreview from "./CanvasPreview";

const ThemeSelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
  flex-shrink: 0;
  width: 40%;
`;

const ThemeCreationContainer = styled.div`
  background: white;
  width: 100%;
  padding: 20px 30px 0 40px;

  > * {
    margin-top: 0;
    margin-bottom: 20px;
  }

  h2 {
    font-size: 24px;
    line-height: 24px;
  }

  p {
    font-size: 14px;
    line-height: 14px;
  }
`;

const ThumbnailScrollBox = styled(ScrollBox)`
  background: white;
  width: 100%;
  padding: 0 30px 20px 40px;
`;

const PalettePopup = styled.div`
  label {
    text-transform: uppercase;
    margin-bottom: 10px;
  }  
  display: grid;
  grid-template-columns: repeat(5,1fr);
  column-gap: 10px;
  row-gap: 10px;
  padding: 30px;
  & > div {
    padding: 10px;
  }
  & > div:hover {
    background: ${themeColors.lightBlue};
  }
  
`;
const Palette = styled.div`
  display: flex;
  
  & > div {
    border-radius:50%;
    width: 18px;
    height: 18px;
    margin-right: 5px;
  } 
`;

const SimpleLabel = styled.label`
  font-size: 14px;
  color: #333;
`;

export class ChooseThemePane extends Component {
    static defaultProps = {
        createButtonText: "Create Presentation"
    }

    state = {
        selectedTheme: null,
        selectedThemeType: null,
        teamThemes: [],
        userThemes: []
    }

    componentDidMount() {
        this.loadTeamThemes();
        this.loadUserThemes();
        this.setDefaultTheme();
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            teamThemes,
            userThemes,
            selectedTheme,
        } = this.state;
        if (!selectedTheme && !_.isEqual(teamThemes, prevState.teamThemes) || !_.isEqual(userThemes, prevState.userThemes)) {
            this.setDefaultTheme();
        }
    }

    setDefaultTheme() {
        const { teamThemes, userThemes } = this.state;
        if (teamThemes?.length > 0) {
            this.setCurrentTheme(teamThemes[0], "team-theme");
        } else if (userThemes?.length > 0) {
            this.setCurrentTheme(userThemes[0], "existing-user-theme");
        } else {
            this.setCurrentTheme(ds.builtInThemes.at(0), "built-in-theme");
        }
    }

    loadTeamThemes() {
        const teamThemes = ds.sharedThemes
            .where({ orgId: UIController.getOrganizationId() })
            .filter(t => !t.get("hidden")) // filter out hidden themes
            .sort((a, b) => a.id.localeCompare(b.id)); // lexicographic sort

        this.setState({ teamThemes });
    }

    loadUserThemes() {
        const userThemes = ds.userThemes
            .where({ orgId: UIController.getOrganizationId() })
            .filter(theme => !theme.get("private"));
        this.setState({ userThemes });
    }

    async setCurrentTheme(theme, themeType) {
        const predefinedPalette = _.find(PREDEFINED_PALETTES, { id: theme.get("palette_name") || "colorful" });
        if (predefinedPalette) {
            theme.set("colors", predefinedPalette.colors);
        }

        this.setState({
            selectedTheme: theme,
            selectedThemeType: themeType,
        });

        theme.loadTheme()
            .then(() => {
                app.currentTheme = theme;
                this.setState({ previewTheme: theme });
            })
            .catch(err => log.error(err));
    }

    handleChooseTheme(theme, themeType) {
        const { onCreate } = this.props;
        onCreate(theme);
        trackActivity("Presentation", "Start", null, null, { source: themeType, theme_id: theme.id }, { audit: false });
    }

    handleDeleteTheme = theme => {
        ShowConfirmationDialog({
            title: "Are you sure you want to delete this theme?",
            message: "No existing presentations will be affected by this change but you won't be able to use this theme in any future presentations."
        }).then(confirmed => {
            if (confirmed) {
                theme.collection.remove([theme]);
                theme.destroy();
                this.loadUserThemes();
            }
        });
    }

    handleClickPalette = event => {
        if (!this.props.requireSharedTheme) {
            this.setState({ anchorEl: event.currentTarget });
        }
    }

    handleSetPalette = palette => {
        this.setState({ anchorEl: null });
        const theme = new Theme(this.state.selectedTheme.attributes, { disconnected: true, autoLoad: false });
        theme.update({ palette_name: palette.id }, { replaceKeys: true });
        this.setCurrentTheme(theme, this.state.selectedThemeType);
    }

    handleNewTheme = async () => {
        let theme = new Theme(defaultSimpleTheme, { disconnected: true, autoLoad: true });
        await theme.loadTheme();
        const editDialog = new EditThemePanel({
            theme,
            isNewTheme: true,
            onSuccess: theme => {
                let {
                    onCreate,
                } = this.props;
                onCreate(theme);
            }
        });
        editDialog.show();
        trackActivity("Presentation", "Start", null, null, { source: "new-user-theme", theme_id: theme.id }, { audit: false });
    }

    handleImportThemeFromPPT = async () => {
        if (await ShowConfirmationDialog({
            title: "Import colors and fonts from a PowerPoint",
            message: "Start with your brand colors and fonts in place, and customize your theme from there.",
            okButtonLabel: "Select File",
        })) {
            let theme = await importPPTTheme("new-presentation");
            if (theme) {
                const editDialog = new EditThemePanel({
                    theme,
                    isNewTheme: true,
                    hideImportTheme: true,
                    onSuccess: theme => {
                        let {
                            onCreate,
                        } = this.props;
                        onCreate(theme);
                    },
                });
                editDialog.show();
                trackActivity("Presentation", "Start", null, null, { source: "ppt-theme", theme_id: theme.id }, { audit: false });
            }
        }
    }

    render() {
        const {
            requireSharedTheme,
            createButtonText,
        } = this.props;
        const {
            selectedTheme,
            selectedThemeType,
            previewTheme,
            anchorEl,
            teamThemes,
            userThemes,
        } = this.state;

        const palette = {};
        if (selectedTheme) {
            _.map(selectedTheme.get("colors"), (color, key) => {
                if (key == "theme" || key.startsWith("accent")) {
                    palette[key] = color;
                }
            });
        }

        return (
            <UIPane>
                <UIPaneContents style={{ padding: 0, background: themeColors.lightGray }}>
                    <FlexBox fill left top>
                        <ThemeSelectContainer>
                            <ThemeCreationContainer>
                                <h2>Select a Theme</h2>
                                <p>Themes define the look of your presentation.</p>
                                <FlexBox left>
                                    <Button
                                        variant="contained"
                                        onClick={this.handleNewTheme}
                                    >
                                        <Icon>add_circle</Icon>
                                        <span>New Theme...</span>
                                    </Button>
                                    <Gap20 />
                                    <Button
                                        variant="contained"
                                        onClick={this.handleImportThemeFromPPT}
                                    >
                                        <Icon>swap_vert</Icon>
                                        <span>Import from PPT...</span>
                                    </Button>
                                </FlexBox>
                            </ThemeCreationContainer>
                            <ThumbnailScrollBox>
                                {teamThemes.length > 0 &&
                                    <Fragment>
                                        <SimpleLabel>TEAM THEMES</SimpleLabel>
                                        <Gap30 />
                                        <ThumbnailGrid
                                            items={teamThemes}
                                            colGap={30}
                                            rowGap={30}
                                            selectedItem={selectedTheme}
                                            thumbnailClass={ThemeThumbnail}
                                            onItemSelected={theme => this.setCurrentTheme(theme, "team-theme")}
                                            onItemDoubleClick={theme => this.handleChooseTheme(theme, "team-theme")}
                                            style={{ gridTemplateColumns: "repeat(2, 1fr)" }} // override the built-in media-query logic
                                        />
                                        <Gap30 />
                                    </Fragment>
                                }

                                {!requireSharedTheme && userThemes.length > 0 &&
                                    <Fragment>
                                        <SimpleLabel>YOUR THEMES</SimpleLabel>
                                        <Gap30 />
                                        <ThumbnailGrid
                                            items={userThemes}
                                            colGap={30}
                                            rowGap={30}
                                            selectedItem={selectedTheme}
                                            thumbnailClass={ThemeThumbnail}
                                            onItemSelected={theme => this.setCurrentTheme(theme, "existing-user-theme")}
                                            onItemDoubleClick={theme => this.handleChooseTheme(theme, "existing-user-theme")}
                                            thumbnailProps={{
                                                onDeleteTheme: this.handleDeleteTheme
                                            }}
                                            style={{ gridTemplateColumns: "repeat(2, 1fr)" }} // override the built-in media-query logic
                                        />
                                        <Gap30 />
                                    </Fragment>
                                }
                                {!requireSharedTheme &&
                                    <Fragment>
                                        <SimpleLabel>BUILT-IN THEMES</SimpleLabel>
                                        <Gap30 />
                                        <ThumbnailGrid
                                            items={ds.builtInThemes}
                                            colGap={30}
                                            rowGap={30}
                                            selectedItem={selectedTheme}
                                            thumbnailClass={ThemeThumbnail}
                                            onItemSelected={theme => this.setCurrentTheme(theme, "built-in-theme")}
                                            onItemDoubleClick={theme => this.handleChooseTheme(theme, "built-in-theme")}
                                            style={{ gridTemplateColumns: "repeat(2, 1fr)" }} // override the built-in media-query logic
                                        />
                                    </Fragment>
                                }
                            </ThumbnailScrollBox>
                        </ThemeSelectContainer>
                        <Gap30 />
                        <FlexBox vertical left fill style={{ paddingBottom: 20 }}>
                            {previewTheme &&
                                <ThemePreview theme={previewTheme} />
                            }
                            <FlexSpacer />
                            <FlexBox fillWidth style={{ paddingRight: 20 }}>
                                {!(selectedTheme instanceof SharedTheme) && (
                                    <>
                                        <Gap20 />
                                        <SimpleLabel>PALETTE</SimpleLabel>
                                        <Gap20 />
                                        <Palette onClick={this.handleClickPalette}>
                                            {Object.keys(palette).map(color => (<div
                                                key={_.uniqueId()} style={{ background: palette[color] }} />))}
                                        </Palette>
                                        <Popover
                                            disableEnforceFocus
                                            open={Boolean(anchorEl)}
                                            anchorEl={anchorEl}
                                            anchorOrigin={{
                                                vertical: "top",
                                                horizontal: "left"
                                            }}
                                            transformOrigin={{
                                                vertical: "top",
                                                horizontal: "left"
                                            }}
                                            onClose={() => this.setState({ anchorEl: null })}
                                        >
                                            <PalettePopup>
                                                {this.renderPalettes()}
                                            </PalettePopup>
                                        </Popover>
                                    </>
                                )}
                                <FlexSpacer />
                                <BlueButton
                                    id="create-presentation-btn"
                                    disabled={!selectedTheme}
                                    onClick={() => this.handleChooseTheme(selectedTheme, selectedThemeType)}
                                >
                                    {createButtonText}
                                </BlueButton>
                            </FlexBox>
                        </FlexBox>

                    </FlexBox>
                </UIPaneContents>
            </UIPane>
        );
    }

    renderPalettes = () => {
        return PREDEFINED_PALETTES.map((palette, index) => {
            const colors = Object.keys(palette.colors);
            return (
                <FlexBox
                    vertical left
                    key={index}
                    onClick={() => this.handleSetPalette(palette)}
                >
                    <label>{palette.name}</label>
                    <Palette>
                        {colors.map((color, index) => {
                            if (color == "theme" || color.startsWith("accent")) {
                                return (
                                    <div
                                        key={index}
                                        style={{ background: palette.colors[color] }}
                                    />
                                );
                            }
                        })}
                    </Palette>
                </FlexBox>
            );
        });
    }
}

const PreviewPane = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  flex-shrink: 1;
  padding: 30px 30px;
`;

class ThemePreview extends Component {
    state = {
        selectedIndex: 0,
        transition: false
    }

    constructor() {
        super();

        this.ref = React.createRef();
    }

    componentDidMount() {
        this.forceUpdate();
        $(window).on("resize.preview", () => {
            this.forceUpdate();
        });
    }

    componentWillUnmount() {
        $(window).off(".preview");
    }

    handleClick = index => {
        this.setState({ selectedIndex: index, transition: true });
    }

    render() {
        const { selectedIndex, transition } = this.state;
        const { theme } = this.props;

        const width = $(this.ref.current).width();
        const height = $(this.ref.current).height();

        let scale = width / 1280;
        let smallCanvasScale = (width - 40) / 1280 / 4;

        const previewHeight = 720 * scale + 10 + 720 * smallCanvasScale;
        if (previewHeight > height - 50) {
            scale *= (height - 50) / previewHeight;
            smallCanvasScale *= (height - 50) / previewHeight;
        }

        const frameTransforms = [];
        let x = 0;
        for (let i = 0; i < 5; i++) {
            if (i == selectedIndex) {
                frameTransforms.push({ x: 0, y: 40, scale });
            } else {
                frameTransforms.push({ x, y: 720 * scale + 55, scale: smallCanvasScale });
                x += 1280 * smallCanvasScale + 15;
            }
        }

        return (
            <PreviewPane ref={this.ref}>
                <SimpleLabel>THEME PREVIEW</SimpleLabel>
                <CanvasPreview onClick={() => this.handleClick(0)}
                    translateX={frameTransforms[0].x}
                    translateY={frameTransforms[0].y}
                    scale={frameTransforms[0].scale} theme={theme} transition={transition}
                    slideModel={PreviewSlide1} />
                <CanvasPreview onClick={() => this.handleClick(1)}
                    translateX={frameTransforms[1].x}
                    translateY={frameTransforms[1].y}
                    scale={frameTransforms[1].scale} theme={theme} transition={transition}
                    slideModel={PreviewSlide2} />
                <CanvasPreview onClick={() => this.handleClick(2)}
                    translateX={frameTransforms[2].x}
                    translateY={frameTransforms[2].y}
                    scale={frameTransforms[2].scale} theme={theme} transition={transition}
                    slideModel={PreviewSlide3} />
                <CanvasPreview onClick={() => this.handleClick(3)}
                    translateX={frameTransforms[3].x}
                    translateY={frameTransforms[3].y}
                    scale={frameTransforms[3].scale} theme={theme} transition={transition}
                    slideModel={PreviewSlide4} />
                <CanvasPreview onClick={() => this.handleClick(4)}
                    translateX={frameTransforms[4].x}
                    translateY={frameTransforms[4].y}
                    scale={frameTransforms[4].scale} theme={theme} transition={transition}
                    slideModel={PreviewSlide5} />
            </PreviewPane>

        );
    }
}
